<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { API_ENDPOINTS, mapDataToWidget } from '../utils/apiConnectors';
import axios from 'axios';
import Auth from 'dashboard/api/auth';

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
  accountId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['update', 'close']);
const { t } = useI18n();

const selectedEndpoint = ref(null);
const endpointParams = ref({});
const testing = ref(false);
const testResult = ref(null);
const error = ref(null);
const showAdvanced = ref(false);

const localWidget = ref(JSON.parse(JSON.stringify(props.widget)));

const categoryIcons = {
  conversations: 'i-lucide-message-square',
  contacts: 'i-lucide-users',
  inboxes: 'i-lucide-mailbox',
  orders: 'i-lucide-shopping-cart',
  custom: 'i-lucide-code',
};

const categoryColors = {
  conversations: 'blue',
  contacts: 'purple',
  inboxes: 'emerald',
  orders: 'teal',
  custom: 'slate',
};

// Initialiser avec la config existante
watch(
  () => props.widget,
  newWidget => {
    localWidget.value = JSON.parse(JSON.stringify(newWidget));
    if (newWidget.props?.apiConnector) {
      const connector = newWidget.props.apiConnector;
      selectedEndpoint.value =
        API_ENDPOINTS.find(e => e.id === connector.endpointId) ||
        API_ENDPOINTS.find(e => e.id === 'custom');
      endpointParams.value = connector.params || {};
    }
  },
  { immediate: true }
);

const hasConnector = computed(() => !!localWidget.value.props?.apiConnector);

const endpointOptions = computed(() => {
  const grouped = {};
  API_ENDPOINTS.forEach(endpoint => {
    if (!grouped[endpoint.category]) {
      grouped[endpoint.category] = [];
    }
    grouped[endpoint.category].push(endpoint);
  });
  return grouped;
});

const selectEndpoint = endpoint => {
  selectedEndpoint.value = endpoint;
  endpointParams.value = {};

  // Initialiser les paramètres par défaut
  endpoint.params?.forEach(param => {
    if (param.default !== undefined) {
      endpointParams.value[param.key] =
        typeof param.default === 'function' ? param.default() : param.default;
    }
  });

  updateConnector();
};

const updateParam = (key, value) => {
  endpointParams.value[key] = value;
  updateConnector();
};

const updateConnector = () => {
  if (!selectedEndpoint.value) {
    // Supprimer le connecteur si aucun endpoint n'est sélectionné
    if (localWidget.value.props?.apiConnector) {
      delete localWidget.value.props.apiConnector;
      emit('update', { ...localWidget.value });
    }
    return;
  }

  localWidget.value.props = {
    ...localWidget.value.props,
    apiConnector: {
      endpointId: selectedEndpoint.value.id,
      endpoint: selectedEndpoint.value.endpoint,
      method: selectedEndpoint.value.method,
      params: { ...endpointParams.value },
    },
  };

  emit('update', { ...localWidget.value });
};

const removeConnector = () => {
  if (
    confirm('Supprimer le connecteur API ? Les données deviendront statiques.')
  ) {
    selectedEndpoint.value = null;
    endpointParams.value = {};
    if (localWidget.value.props?.apiConnector) {
      delete localWidget.value.props.apiConnector;
      emit('update', { ...localWidget.value });
    }
  }
};

const getAuthHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };

  if (Auth.hasAuthCookie()) {
    const {
      'access-token': accessToken,
      'token-type': tokenType,
      client,
      expiry,
      uid,
    } = Auth.getAuthData();

    if (accessToken) {
      headers['access-token'] = accessToken;
      headers['token-type'] = tokenType;
      headers.client = client;
      headers.expiry = expiry;
      headers.uid = uid;
    }
  }

  return headers;
};

const testConnection = async () => {
  if (!selectedEndpoint.value) return;

  testing.value = true;
  error.value = null;
  testResult.value = null;

  try {
    const endpointConfig =
      selectedEndpoint.value.id === 'custom'
        ? {
            endpoint: endpointParams.value.endpoint || '',
            method: endpointParams.value.method || 'GET',
            dataMapper: {},
          }
        : selectedEndpoint.value;

    const url = endpointConfig.endpoint.replace('{accountId}', props.accountId);
    const queryParams = new URLSearchParams();

    Object.entries(endpointParams.value).forEach(([key, value]) => {
      if (
        value !== null &&
        value !== undefined &&
        value !== '' &&
        key !== 'endpoint' &&
        key !== 'method'
      ) {
        queryParams.append(key, value);
      }
    });

    const fullUrl = queryParams.toString()
      ? `${url}?${queryParams.toString()}`
      : url;

    const response = await axios.get(fullUrl, {
      headers: getAuthHeaders(),
    });
    const data = response.data;

    testResult.value = {
      url: fullUrl,
      raw: data,
      mapped: null,
    };

    // Prévisualiser le mapping
    if (
      endpointConfig.dataMapper &&
      endpointConfig.dataMapper[props.widget.type]
    ) {
      const mapped = mapDataToWidget(
        data,
        endpointConfig.dataMapper,
        props.widget.type
      );
      testResult.value.mapped = mapped;
    }
  } catch (err) {
    error.value =
      err.response?.data?.error ||
      err.message ||
      'Erreur lors du test de connexion';
  } finally {
    testing.value = false;
  }
};

const formatTimestamp = ts => {
  if (!ts) return '';
  const date = new Date(ts * 1000);
  return date.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getParamHelper = param => {
  if (param.key === 'since' || param.key === 'until') {
    return 'Timestamp Unix (secondes depuis 1970)';
  }
  return null;
};
</script>

<template>
  <div class="space-y-6">
    <!-- Header avec état actuel -->
    <div
      class="flex items-center justify-between p-4 rounded-lg"
      :class="
        hasConnector
          ? 'bg-n-teal-2 border border-n-teal-6'
          : 'bg-n-slate-2 border border-n-weak'
      "
    >
      <div class="flex items-center gap-3">
        <div
          class="p-2 rounded-lg"
          :class="hasConnector ? 'bg-n-teal-3' : 'bg-n-slate-3'"
        >
          <span
            :class="
              hasConnector
                ? 'i-lucide-plug-2 size-5 text-n-teal-11'
                : 'i-lucide-plug size-5 text-n-slate-11'
            "
          />
        </div>
        <div>
          <div class="text-sm font-semibold text-n-slate-12">
            {{
              hasConnector
                ? 'Connecteur API actif'
                : 'Aucun connecteur configuré'
            }}
          </div>
          <div class="text-xs text-n-slate-10">
            {{
              hasConnector
                ? 'Les données sont récupérées automatiquement'
                : 'Configurez un connecteur pour des données dynamiques'
            }}
          </div>
        </div>
      </div>
      <button
        v-if="hasConnector"
        class="p-2 rounded-lg hover:bg-n-ruby-3 text-n-ruby-11 transition-colors"
        title="Supprimer le connecteur"
        @click="removeConnector"
      >
        <span class="i-lucide-x size-4" />
      </button>
    </div>

    <!-- Sélection d'endpoint -->
    <div>
      <label class="block text-sm font-semibold text-n-slate-12 mb-3">Sélectionner une source de données</label>

      <div class="space-y-4">
        <div
          v-for="(endpoints, category) in endpointOptions"
          :key="category"
          class="space-y-2"
        >
          <div class="flex items-center gap-2 mb-2">
            <span
              :class="categoryIcons[category] || 'i-lucide-folder'"
              class="size-4 text-n-slate-11"
            />
            <span
              class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
            >
              {{ category }}
            </span>
          </div>
          <div class="grid gap-3">
            <button
              v-for="endpoint in endpoints"
              :key="endpoint.id"
              class="group p-4 text-left border-2 rounded-xl transition-all relative overflow-hidden"
              :class="
                selectedEndpoint?.id === endpoint.id
                  ? 'border-n-blue-9 bg-n-blue-1 shadow-md'
                  : 'border-n-weak hover:border-n-blue-6 bg-n-solid-1 hover:shadow-sm'
              "
              @click="selectEndpoint(endpoint)"
            >
              <!-- Indicateur de sélection -->
              <div
                v-if="selectedEndpoint?.id === endpoint.id"
                class="absolute top-0 right-0 p-1.5 bg-n-blue-9 text-white rounded-bl-lg"
              >
                <span class="i-lucide-check size-3" />
              </div>

              <div class="flex items-start gap-3 pr-8">
                <div
                  class="p-2.5 rounded-lg flex-shrink-0 transition-colors"
                  :class="
                    selectedEndpoint?.id === endpoint.id
                      ? 'bg-n-blue-3'
                      : 'bg-n-slate-2 group-hover:bg-n-blue-2'
                  "
                >
                  <span
                    class="size-5"
                    :class="[
                      categoryIcons[category] || 'i-lucide-plug',
                      selectedEndpoint?.id === endpoint.id
                        ? 'text-n-blue-11'
                        : 'text-n-slate-11',
                    ]"
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-1">
                    <div class="text-sm font-semibold text-n-slate-12">
                      {{ endpoint.name }}
                    </div>
                    <span
                      v-if="endpoint.method"
                      class="px-1.5 py-0.5 text-[10px] font-mono font-semibold rounded"
                      :class="
                        selectedEndpoint?.id === endpoint.id
                          ? 'bg-n-blue-3 text-n-blue-11'
                          : 'bg-n-slate-2 text-n-slate-10'
                      "
                    >
                      {{ endpoint.method }}
                    </span>
                  </div>
                  <div class="text-xs text-n-slate-10 leading-relaxed mb-2">
                    {{ endpoint.description }}
                  </div>
                  <div
                    class="flex items-center gap-1.5 text-xs text-n-slate-9 font-mono bg-n-alpha-1 px-2 py-1 rounded"
                  >
                    <span class="i-lucide-link size-3" />
                    <span class="truncate">{{ endpoint.endpoint }}</span>
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Configuration des paramètres -->
    <div
      v-if="selectedEndpoint && selectedEndpoint.params?.length > 0"
      class="space-y-4"
    >
      <div class="flex items-center justify-between">
        <label class="block text-sm font-semibold text-n-slate-12">Paramètres de requête</label>
        <button
          class="text-xs text-n-blue-11 hover:text-n-blue-12 flex items-center gap-1"
          @click="showAdvanced = !showAdvanced"
        >
          <span
            :class="
              showAdvanced ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
            "
            class="size-3"
          />
          {{ showAdvanced ? 'Masquer' : 'Afficher' }} les détails
        </button>
      </div>

      <div class="space-y-3 p-4 bg-n-alpha-1 rounded-lg border border-n-weak">
        <div
          v-for="param in selectedEndpoint.params"
          :key="param.key"
          class="space-y-1.5"
        >
          <div class="flex items-center justify-between">
            <label class="block text-xs font-medium text-n-slate-11">
              {{ param.label }}
              <span v-if="param.optional"
class="text-n-slate-9 font-normal"
                >(optionnel)</span>
            </label>
            <span
              v-if="getParamHelper(param)"
              class="text-[10px] text-n-slate-9"
              :title="getParamHelper(param)"
            >
              <span class="i-lucide-info size-3" />
            </span>
          </div>

          <select
            v-if="param.type === 'select'"
            :value="endpointParams[param.key] || param.default"
            class="w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-n-blue-9 transition-all"
            @change="updateParam(param.key, $event.target.value)"
          >
            <option
              v-for="option in param.options"
              :key="option"
              :value="option"
            >
              {{ option }}
            </option>
          </select>

          <div v-else class="relative">
            <Input
              :model-value="endpointParams[param.key] || param.default || ''"
              :type="param.type === 'number' ? 'number' : 'text'"
              :placeholder="
                param.placeholder || `Entrez ${param.label.toLowerCase()}`
              "
              @update:model-value="updateParam(param.key, $event)"
            />
            <div
              v-if="param.key === 'since' || param.key === 'until'"
              class="absolute right-2 top-1/2 -translate-y-1/2"
            >
              <button
                class="text-xs text-n-blue-11 hover:text-n-blue-12 px-2 py-1 rounded hover:bg-n-blue-1"
                @click="
                  updateParam(
                    param.key,
                    Math.floor(Date.now() / 1000) -
                      (param.key === 'since' ? 86400 * 30 : 0)
                  )
                "
              >
                Maintenant
              </button>
            </div>
          </div>

          <div
            v-if="
              showAdvanced && param.key === 'since' && endpointParams[param.key]
            "
            class="text-xs text-n-slate-9 pl-1"
          >
            {{ formatTimestamp(endpointParams[param.key]) }}
          </div>
          <div
            v-if="
              showAdvanced && param.key === 'until' && endpointParams[param.key]
            "
            class="text-xs text-n-slate-9 pl-1"
          >
            {{ formatTimestamp(endpointParams[param.key]) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Endpoint personnalisé -->
    <div
      v-if="selectedEndpoint?.id === 'custom'"
      class="space-y-3 p-4 bg-n-alpha-1 rounded-lg border border-n-weak"
    >
      <div>
        <label class="block text-xs font-medium text-n-slate-11 mb-1.5">URL de l'endpoint</label>
        <Input
          :model-value="endpointParams.endpoint || ''"
          placeholder="/api/v1/accounts/{accountId}/..."
          @update:model-value="updateParam('endpoint', $event)"
        />
        <p class="text-xs text-n-slate-9 mt-1">
          Utilisez {accountId} comme placeholder
        </p>
      </div>
      <div>
        <label class="block text-xs font-medium text-n-slate-11 mb-1.5">Méthode HTTP</label>
        <select
          :value="endpointParams.method || 'GET'"
          class="w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-n-blue-9"
          @change="updateParam('method', $event.target.value)"
        >
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="PATCH">PATCH</option>
          <option value="DELETE">DELETE</option>
        </select>
      </div>
    </div>

    <!-- Test de connexion -->
    <div v-if="selectedEndpoint" class="pt-4 border-t border-n-weak space-y-3">
      <Button
        label="Tester la connexion"
        icon="i-lucide-test-tube"
        variant="outline"
        color="blue"
        :loading="testing"
        class="w-full"
        @click="testConnection"
      />

      <!-- Erreur -->
      <div
        v-if="error"
        class="p-4 bg-n-ruby-2 border border-n-ruby-6 rounded-lg"
      >
        <div class="flex items-start gap-3">
          <span
            class="i-lucide-alert-circle size-5 text-n-ruby-11 flex-shrink-0 mt-0.5"
          />
          <div class="flex-1">
            <div class="text-sm font-semibold text-n-ruby-11 mb-1">
              Erreur de connexion
            </div>
            <div class="text-xs text-n-ruby-10">{{ error }}</div>
          </div>
        </div>
      </div>

      <!-- Succès -->
      <div
        v-if="testResult"
        class="p-4 bg-n-teal-2 border border-n-teal-6 rounded-lg space-y-3"
      >
        <div class="flex items-start gap-3">
          <span
            class="i-lucide-check-circle size-5 text-n-teal-11 flex-shrink-0 mt-0.5"
          />
          <div class="flex-1">
            <div class="text-sm font-semibold text-n-teal-11 mb-1">
              Connexion réussie
            </div>
            <div class="text-xs text-n-teal-10 font-mono break-all">
              {{ testResult.url }}
            </div>
          </div>
        </div>

        <!-- Données mappées -->
        <div
          v-if="testResult.mapped"
          class="mt-3 p-3 bg-n-solid-1 rounded-lg border border-n-weak"
        >
          <div class="text-xs font-semibold text-n-slate-12 mb-2">
            Données mappées pour ce widget :
          </div>
          <div class="text-xs text-n-slate-11 space-y-1">
            <div
              v-for="(value, key) in testResult.mapped"
              :key="key"
              class="flex items-center gap-2"
            >
              <span class="font-mono text-n-slate-9">{{ key }}:</span>
              <span class="font-semibold">{{ value }}</span>
            </div>
          </div>
        </div>

        <!-- Données brutes -->
        <details class="mt-2">
          <summary
            class="text-xs font-medium text-n-slate-11 cursor-pointer hover:text-n-slate-12"
          >
            Aperçu des données brutes
          </summary>
          <pre
            class="mt-2 p-3 bg-n-solid-1 rounded text-xs overflow-auto max-h-60 border border-n-weak"
            >{{ JSON.stringify(testResult.raw, null, 2) }}</pre>
        </details>
      </div>
    </div>
  </div>
</template>
